import React, { useEffect, useState } from "react";
import AuditLogTable from "./logs/audit-log-table";
import XanoAPI from "../../../lib/XanoAPI";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Icons } from "../../../utils/icons";
import { useForm, FormProvider } from "react-hook-form";
import SelectController from "../../form-fields/SelectController";
import { useAuth } from "../../../lib/AuthContext";

interface AuditLogEntry {
  action: string;
  created_at: number;
  id: string;
  object: string;
  target: string;
  user_id: number;
  _user_details: {
    name: string;
    email: string;
    permissions_id: number;
    status: string;
  };
}

interface AuditLogResponse {
  curPage: number;
  itemsReceived: number | null;
  itemsTotal: number | null;
  nextPage: number | null;
  offset: number;
  items: AuditLogEntry[];
  pageTotal: number;
  perPage: number;
  prevPage: number;
}

const AuditLog: React.FC = () => {
  const { currentUser } = useAuth();
  const [auditLogs, setAuditLogs] = useState<AuditLogResponse>({
    curPage: 1,
    itemsReceived: 0,
    itemsTotal: 0,
    nextPage: null,
    prevPage: 0,
    offset: 0,
    pageTotal: 1,
    perPage: 10,
    items: [],
  });
  const [filteredLogs, setFilteredLogs] = useState<AuditLogEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const methods = useForm({
    defaultValues: {
      selectedUser: "",
    },
  });

  const fetchAuditLogs = async (page = 1, search = "") => {
    try {
      const api = XanoAPI.getInstance();

      // Determine user role and appropriate parameters
      console.log("🔍 ROLE DETECTION DEBUG:");
      console.log("🔍 currentUser:", currentUser);
      console.log(
        "🔍 currentUser?.user_permissions:",
        currentUser?.user_permissions
      );
      console.log(
        "🔍 currentUser?.user_permissions?.[0]:",
        currentUser?.user_permissions?.[0]
      );
      console.log(
        "🔍 currentUser?.user_permissions?.[0]?.role:",
        currentUser?.user_permissions?.[0]?.role
      );

      const userRole = currentUser?.user_permissions?.[0]?.role || "unknown";
      let clientId: number | string | undefined;

      if (userRole === "agent") {
        // Agent users: use their user ID as client_id
        clientId = currentUser?.id;
      }

      console.log(`✅ Fetching Audit Logs for page ${page}...`);
      console.log(`✅ User role: ${userRole}`);
      console.log(`✅ Client ID: ${clientId}`);
      console.log(`✅ Search term: ${search}`);
      console.log(`✅ Current user object:`, currentUser);
      console.log(`✅ Current user role object:`, currentUser?.role);

      const apiParams = {
        page,
        search,
        userRole,
        clientId,
      };
      console.log(`✅ API parameters being passed:`, apiParams);

      const data = await api.getAuditLog(apiParams);

      console.log("📌 Audit Logs API Response:", data);
      setAuditLogs(data);
      setFilteredLogs(data.items); // Initialize filtered logs
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    }
  };

  useEffect(() => {
    fetchAuditLogs(currentPage, searchTerm);
  }, [currentPage, searchTerm]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    fetchAuditLogs(1, term); // Reset to the first page with the search query
  };

  const handleUserFilter = (user: string) => {
    const filtered = user
      ? auditLogs.items.filter((log) => log._user_details.name === user)
      : auditLogs.items;

    setFilteredLogs(filtered);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const userOptions = Array.from(
    new Set(auditLogs.items.map((log) => log._user_details.name))
  ).map((name) => ({ value: name, label: name }));

  return (
    <FormProvider {...methods}>
      <div className="py-6">
        <h2 className="text-xl font-semibold text-accent mb-4">Audit Log</h2>

        {/* Search Bar and User Filter */}
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-2/3">
            <Input
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e: any) => handleSearch(e.target.value)}
              className="flex-grow"
            />
          </div>

          <div className="w-1/3">
            <SelectController
              name="selectedUser"
              label="Filter by User"
              options={[{ value: "", label: "All Users" }, ...userOptions]}
              defaultValue=""
              onChange={(val) => handleUserFilter(val)}
            />
          </div>
        </div>

        {/* Audit Log Table */}
        <AuditLogTable logs={filteredLogs} />

        {/* Pagination Controls */}
        <div className="flex items-center justify-between mt-4">
          <Button
            variant="outline"
            disabled={currentPage <= 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <Icons.arrowLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-gray-500">
            Page {currentPage} of {auditLogs.pageTotal}
          </span>
          <Button
            className=""
            variant="outline"
            disabled={currentPage >= auditLogs.pageTotal}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <Icons.arrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </FormProvider>
  );
};

export default AuditLog;
